import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { ProgressBar, StatusPill } from "@/components/Progress";
import { BrandMark } from "@/components/BrandMark";
import { api } from "@/lib/api-client";
import { mapsUrl, num } from "@/lib/geo";
import { STAGE_LABEL, STATUS_LABEL, useLocale } from "@/lib/locale";
import { cn } from "@/lib/utils";
import type { ProjectDetail } from "@/lib/domain";

const SURFACE_TYPES = ["floor", "wall", "ceiling", "furniture", "wet_area", "exterior"];
const FINISHES = [
  "spatolato",
  "travertino",
  "cemento_industriale",
  "nuvolato",
  "pietra_naturale",
  "calce_antica",
  "epoxy_chromatic",
  "epoxy_metallic",
  "epoxy_art",
  "custom",
];
const SYSTEMS = ["FLEX", "RADIANT", "CLPH", "R10R12", "UVPLUS", "ZERO_YELLOW"];
const COLORS = [
  "Bianco Corda",
  "Perla Antica",
  "Tortora",
  "Grigio Perla",
  "Cemento",
  "Grigio Ferro",
  "Antracite",
  "Terra Andalusa",
  "Terracotta",
  "Cotto Bruciato",
  "Ocra",
  "Oliva",
  "Salvia",
  "Verde Bosco",
  "Blu Petrolio",
  "Blu Notte",
  "Prugna",
];

function MiniTimeline({ statuses }: { statuses: string[] }) {
  return (
    <div className="flex gap-1">
      {statuses.map((s, i) => (
        <span
          key={i}
          title={s}
          className={cn(
            "h-1.5 w-6 rounded-full",
            s === "approved"
              ? "bg-green"
              : s === "reported"
                ? "bg-pink"
                : s === "issue"
                  ? "bg-rose"
                  : s === "in_progress"
                    ? "bg-gold"
                    : "bg-white/15",
          )}
        />
      ))}
    </div>
  );
}

export default function ProjectDetailPage() {
  const { id = "" } = useParams();
  const { t, locale } = useLocale();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [showSurface, setShowSurface] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    () => api.project(id).then(setProject).catch(() => setProject(null)),
    [id],
  );

  useEffect(() => {
    void load();
  }, [load]);

  async function onCreateSurface(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.createSurface(id, {
        name: form.get("name"),
        surfaceType: form.get("surfaceType"),
        m2: Number(form.get("m2")),
        finishEffect: form.get("finishEffect"),
        colorRef: form.get("colorRef"),
        systemVariants: SYSTEMS.filter((s) => form.get(`sys-${s}`) === "on"),
      });
      setShowSurface(false);
      await load();
    } catch {
      setError("Creazione non riuscita.");
    } finally {
      setBusy(false);
    }
  }

  if (!project) {
    return (
      <AppShell>
        <p className="text-sm text-ice/60">Caricamento…</p>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Link
        to="/projects"
        className="text-[0.62rem] uppercase tracking-[0.18em] text-ice/55 hover:text-paper"
      >
        ← {t.back}
      </Link>

      <header className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="brand-kicker">{project.client.name}</div>
          <h1 className="font-serif text-2xl text-paper sm:text-3xl">
            {project.name}
          </h1>
          <p className="mt-1 text-[0.76rem] text-ice/65">
            {project.siteAddress} · {project.country}
            {project.totalM2 ? ` · ${project.totalM2} m²` : ""}
            {num(project.lat) !== null && num(project.lng) !== null && (
              <>
                {" · "}
                <a href={mapsUrl(num(project.lat)!, num(project.lng)!)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-goldbright hover:underline">
                  <MapPin className="h-3 w-3" /> Open in Google Maps
                </a>
              </>
            )}
          </p>
        </div>
        <div className="min-w-[220px]">
          <StatusPill
            status={project.status}
            label={STATUS_LABEL[locale][project.status] ?? project.status}
            className="mb-2"
          />
          <ProgressBar progress={project.progress} />
        </div>
      </header>

      {project.notes && (
        <section className="glass-panel mt-5 p-4">
          <div className="brand-kicker mb-2">Notes</div>
          <pre className="whitespace-pre-wrap font-sans text-[0.8rem] leading-relaxed text-ice/80">{project.notes}</pre>
        </section>
      )}

      <hr className="hairline my-6" />

      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-goldbright">
          {t.surfaces}
        </h2>
        <button
          type="button"
          onClick={() => setShowSurface((v) => !v)}
          className="rounded-full border border-white/15 px-3 py-1.5 text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-ice/70 hover:text-paper"
        >
          {showSurface ? "×" : "+ "}
          {t.newSurface}
        </button>
      </div>

      {showSurface && (
        <form
          onSubmit={onCreateSurface}
          className="glass-panel-strong mt-4 grid gap-3 p-5 sm:grid-cols-3"
        >
          <label className="block sm:col-span-3">
            <span className="field-label">Nome superficie</span>
            <input name="name" required className="field" placeholder="Lobby & reception floor" />
          </label>
          <label className="block">
            <span className="field-label">Tipologia</span>
            <select name="surfaceType" className="field" defaultValue="floor">
              {SURFACE_TYPES.map((s) => (
                <option key={s} value={s} className="bg-night">
                  {s.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="field-label">Area (m²)</span>
            <input name="m2" type="number" step="0.01" required className="field num" />
          </label>
          <label className="block">
            <span className="field-label">Finitura</span>
            <select name="finishEffect" className="field" defaultValue="spatolato">
              {FINISHES.map((f) => (
                <option key={f} value={f} className="bg-night">
                  {f.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </label>
          <label className="block sm:col-span-2">
            <span className="field-label">Colore — Mediterranean Collection</span>
            <select name="colorRef" className="field" defaultValue="Terra Andalusa">
              {COLORS.map((c) => (
                <option key={c} value={c} className="bg-night">
                  {c}
                </option>
              ))}
            </select>
          </label>
          <div className="sm:col-span-3">
            <span className="field-label">Sistema VERSUS</span>
            <div className="flex flex-wrap gap-1.5">
              {SYSTEMS.map((s) => (
                <label
                  key={s}
                  className="flex items-center gap-2 rounded-full border border-white/15 px-3 py-1.5 text-[0.6rem] uppercase tracking-[0.1em] text-ice/70"
                >
                  <input type="checkbox" name={`sys-${s}`} className="accent-magenta" />
                  {s}
                </label>
              ))}
            </div>
          </div>
          {error && <p className="text-xs text-pink sm:col-span-3">{error}</p>}
          <div className="sm:col-span-3">
            <button
              type="submit"
              disabled={busy}
              className="rounded-full bg-magenta px-5 py-2 text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-paper disabled:opacity-60"
            >
              {busy ? "…" : "Crea superficie"}
            </button>
          </div>
        </form>
      )}

      <div className="mt-5 grid gap-4">
        {project.surfaces.length === 0 && (
          <p className="text-sm text-ice/60">Nessuna superficie registrata.</p>
        )}
        {project.surfaces.map((s) => (
          <Link
            key={s.id}
            to={`/surfaces/${s.id}`}
            className="glass-panel-strong flex flex-wrap items-center gap-4 p-4 transition hover:border-pink/50"
          >
            <BrandMark className="h-9 w-9" />
            <div className="min-w-0 flex-1">
              <div className="truncate font-serif text-base text-paper">
                {s.name}
              </div>
              <div className="mt-0.5 text-[0.68rem] text-ice/60">
                {s.surfaceType.replace(/_/g, " ")} · {s.m2} m² ·{" "}
                {s.finishEffect.replace(/_/g, " ")}
                {s.colorRef ? ` · ${s.colorRef}` : ""}
              </div>
              {s.notes && <div className="mt-1 line-clamp-2 whitespace-pre-line text-[0.68rem] text-ice/50">{s.notes}</div>}
            </div>
            <MiniTimeline statuses={s.stages.map((x) => x.status)} />
            <StatusPill
              status={s.status}
              label={STATUS_LABEL[locale][s.status] ?? s.status}
            />
          </Link>
        ))}
      </div>

      <hr className="hairline my-7" />

      <h2 className="text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-goldbright">
        {t.dayLog}
      </h2>
      <div className="mt-4 grid gap-2">
        {project.days.map((d) => (
          <div
            key={d.id}
            className="glass-panel flex items-center gap-4 p-3.5 text-sm"
          >
            <span className="num text-[0.68rem] text-ice/55">
              {String(d.dayNumber).padStart(2, "0")}
            </span>
            <span className="num text-[0.72rem] text-ice/75">
              {d.date.slice(0, 10)}
            </span>
            <span className="flex-1 truncate text-[0.82rem] text-paper/90">
              {d.notes ?? "—"}
            </span>
            <StatusPill
              status={d.status}
              label={STATUS_LABEL[locale][d.status] ?? d.status}
            />
          </div>
        ))}
        {project.days.length === 0 && (
          <p className="text-sm text-ice/60">Nessuna giornata registrata.</p>
        )}
      </div>

      <p className="mt-4 text-[0.66rem] text-ice/45">
        {STAGE_LABEL[locale].application}: “{t.workReported}” ≠ “{t.reviewApproved}
        ”.
      </p>
    </AppShell>
  );
}
